import React from 'react'
import store from 'state/store'
import { GridActionsCellItem } from '@mui/x-data-grid-pro'
import { IconButton, MenuItem, TextField, Typography } from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import CheckboxCell from 'components/generic/datagrid/cells/CheckboxCell'
import { isDefaultRow } from 'components/containers/settings/utils/helperFunctions.js'
import InputFormat from 'components/generic/inputs/formatting/InputFormat'
import { ACCOUNT_PAYMENT_TYPES, ADJUSTMENT_TYPES, ADJUSTMENT_TYPES_ARRAY, UNIT_IDS, UNITS_ARRAY } from 'utils/constants'
import { FeeOrPromotionSettingResponseBody } from 'gen/rtk-openapi'
import { ToggleButton, ToggleButtonGroup } from '@mui/material_modern'

export const ADJUSTMENT_COLUMNS = ({ apiRef, onDeleteRow }) => {
  const { defaultYardUm } = store.getState()?.settings || {}

  return [
    {
      field: 'display',
      headerName: 'Display',
      flex: 1,
      sortable: false,
      renderCell: params => {
        const { row } = params

        return (
          <TextField
            sx={{ width: '100%' }}
            label=""
            data-test={`${row?.id}-display`}
            value={row?.display}
            variant="standard"
            onChange={event => {
              const { value } = event.target
              apiRef.current?.updateRows([{ ...row, display: value }])
            }}
            InputProps={{
              disableUnderline: true,
              sx: { fontSize: '0.875rem' },
            }}
          />
        )
      },
    },
    {
      field: 'adjustmentType',
      headerName: 'Adjustment Type',
      headerAlign: 'left',
      align: 'left',
      flex: 0.5,
      sortable: false,
      renderCell: params => {
        const { row } = params

        return (
          <TextField
            sx={{ width: '100%' }}
            label=""
            data-test={`${row?.id}-adjustmentType`}
            value={row?.adjustmentType || ''}
            variant="standard"
            onChange={event => {
              const { value } = event.target
              apiRef.current?.updateRows([{ ...row, adjustmentType: value }])
            }}
            InputProps={{
              disableUnderline: true,
              sx: { fontSize: '0.875rem' },
            }}
            select
          >
            {ADJUSTMENT_TYPES_ARRAY.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        )
      },
    },
    {
      field: 'amount',
      headerName: 'Amount',
      headerAlign: 'right',
      align: 'right',
      type: 'number',
      sortable: false,
      renderCell: params => {
        const { row } = params

        return (
          <TextField
            sx={{ width: '100%' }}
            label=""
            data-test={`${row?.id}-amount`}
            value={row?.amount}
            variant="standard"
            onChange={event => {
              const { value } = event.target
              apiRef.current?.updateRows([{ ...row, amount: value }])
            }}
            InputProps={{
              disableUnderline: true,
              inputComponent: InputFormat,
              sx: { fontSize: '0.875rem' },
            }}
          />
        )
      },
    },
    {
      field: 'um',
      headerName: 'UM',
      headerAlign: 'left',
      align: 'left',
      sortable: false,
      renderCell: params => {
        const { row } = params
        const unitsArray = UNITS_ARRAY.filter(({ id }) => {
          if (row?.adjustmentType === ADJUSTMENT_TYPES.WEIGHT) {
            return id !== UNIT_IDS.USD
          } else if (row?.adjustmentType === ADJUSTMENT_TYPES.COST) {
            return id !== UNIT_IDS.POUND
          }
          return true
        }).map(unit => {
          if (unit.id === UNIT_IDS.POUND) return { ...unit, name: defaultYardUm, label: defaultYardUm }
          return unit
        })

        return (
          <TextField
            sx={{ width: '100%' }}
            label=""
            data-test={`${row?.id}-um`}
            value={row?.um || ''}
            variant="standard"
            onChange={event => {
              const { value } = event.target
              apiRef.current?.updateRows([{ ...row, um: value }])
            }}
            InputProps={{
              disableUnderline: true,
              sx: { fontSize: '0.875rem' },
            }}
            select
          >
            {unitsArray.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        )
      },
    },
    {
      field: 'active',
      headerName: 'Active',
      renderCell: CheckboxCell,
      headerAlign: 'center',
      align: 'center',
      sortable: false,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      type: 'actions',
      getActions: params => [
        <GridActionsCellItem
          onClick={event => {
            event.stopPropagation()
            apiRef.current?.updateRows([{ id: params.row?.id, _action: 'delete' }])
            onDeleteRow(params.row?.id)
          }}
          label="Remove"
          showInMenu
        />,
      ],
    },
  ]
}

export const FINISHED_GOODS_COLUMNS = ({ apiRef, onDeleteRow }) => [
  {
    field: 'display',
    headerName: 'Display',
    flex: 1,
    sortable: false,
    renderCell: params => {
      const { row } = params
      return (
        <TextField
          sx={{ width: '100%' }}
          label=""
          data-test={`${row?.id}-display`}
          value={row?.display}
          variant="standard"
          onChange={event => {
            const { value } = event.target
            apiRef.current?.updateRows([{ ...row, display: value }])
          }}
          InputProps={{
            disableUnderline: true,
            sx: { fontSize: '0.875rem' },
          }}
        />
      )
    },
  },
  {
    field: 'defaultTare',
    headerName: 'Default Tare Weight',
    flex: 0.5,
    sortable: false,
    renderCell: params => {
      const { row } = params
      return (
        <TextField
          sx={{ width: '100%' }}
          label=""
          data-test={`${row?.id}-defaultTare`}
          defaultValue={row?.defaultTare}
          variant="standard"
          onChange={event => {
            const { value } = event.target
            apiRef.current?.updateRows([{ id: row.id, defaultTare: value }])
          }}
          InputProps={{
            disableUnderline: true,
            inputComponent: InputFormat,
            inputProps: {
              allowNegative: false,
              decimalScale: 0,
            },
            sx: { fontSize: '0.875rem' },
          }}
        />
      )
    },
  },
  {
    field: 'active',
    headerName: 'Active',
    renderCell: CheckboxCell,
    headerAlign: 'center',
    align: 'center',
    sortable: false,
  },
  {
    field: 'actions',
    headerName: 'Actions',
    type: 'actions',
    getActions: params => [
      <GridActionsCellItem
        onClick={event => {
          event.stopPropagation()
          apiRef.current?.updateRows([{ id: params.row?.id, _action: 'delete' }])
          onDeleteRow(params.row?.id)
        }}
        label="Remove"
        showInMenu
      />,
    ],
  },
]

const PAYMENT_TYPES_ARRAY: FeeOrPromotionSettingResponseBody['eligiblePaymentTypes'] = ['cash', 'check', 'atm', 'eft']
const FEE_OR_PROMOTION_UNITS_ARRAY: FeeOrPromotionSettingResponseBody['amountType'][] = ['FLAT', 'PERCENT']

export const FEE_OR_PROMOTION_COLUMNS = ({ apiRef, onDeleteRow }) => {
  return [
    {
      field: 'name',
      headerName: 'Display',
      flex: 1,
      sortable: false,
      renderCell: params => {
        const { row } = params
        return (
          <TextField
            sx={{ width: '100%' }}
            label=""
            data-test={`${row?.id}-name`}
            value={row?.name}
            variant="standard"
            onChange={event => {
              const { value } = event.target
              apiRef.current?.updateRows([{ ...row, name: value }])
            }}
            InputProps={{
              disableUnderline: true,
              sx: { fontSize: '0.875rem' },
            }}
          />
        )
      },
    },
    {
      field: 'eligiblePaymentTypes',
      headerName: 'Payment Types',
      headerAlign: 'left',
      align: 'left',
      flex: 0.5,
      sortable: false,
      renderCell: params => {
        const { row } = params
        return (
          <TextField
            select
            fullWidth
            variant="standard"
            SelectProps={{
              multiple: true,
              renderValue: selected => {
                const allTypesSelected = PAYMENT_TYPES_ARRAY.every(t => (selected as string[]).includes(t))
                if (allTypesSelected) return 'Not Specified'
                return (selected as string[]).length > 0 ? (selected as string[]).join(', ') : ''
              },
            }}
            value={row.eligiblePaymentTypes || []}
            onChange={e => {
              const value = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value
              apiRef.current?.updateRows([{ ...row, eligiblePaymentTypes: value }])
            }}
            InputProps={{
              disableUnderline: true,
              sx: { fontSize: '0.875rem' },
            }}
          >
            {PAYMENT_TYPES_ARRAY.map(paymentType => (
              <MenuItem key={paymentType} value={paymentType}>
                {paymentType}
              </MenuItem>
            ))}
          </TextField>
        )
      },
    },
    {
      field: 'type',
      headerName: 'Fee / Promotion',
      headerAlign: 'center',
      align: 'center',
      flex: 0.5,
      sortable: false,
      renderCell: params => {
        const { row } = params
        return (
          <ToggleButtonGroup
            value={row.type}
            exclusive
            size="small"
            aria-label="Fee or Promotion"
            sx={{
              '& .MuiToggleButtonGroup-grouped': { margin: 0 },
              '& .MuiToggleButton-root': {
                width: '40px',
                height: '24px',
              },
            }}
            onChange={(_e, newType) => {
              if (newType) {
                params.api.updateRows([{ ...row, type: newType }])
              }
            }}
          >
            <ToggleButton value="FEE">-</ToggleButton>
            <ToggleButton value="PROMOTION">+</ToggleButton>
          </ToggleButtonGroup>
        )
      },
    },
    {
      field: 'amount',
      headerName: 'Amount',
      headerAlign: 'right',
      align: 'right',
      type: 'number',
      sortable: false,
      renderCell: params => {
        const { row } = params

        return (
          <TextField
            sx={{ width: '100%' }}
            label=""
            data-test={`${row?.id}-amount`}
            value={row?.amount}
            variant="standard"
            onChange={event => {
              const { value } = event.target
              apiRef.current?.updateRows([{ ...row, amount: value }])
            }}
            InputProps={{
              disableUnderline: true,
              inputComponent: InputFormat,
              sx: { fontSize: '0.875rem' },
            }}
          />
        )
      },
    },
    {
      field: 'amountType',
      headerName: 'UM',
      flex: 0.3,
      headerAlign: 'left',
      align: 'left',
      sortable: false,
      renderCell: params => {
        const { row } = params

        return (
          <TextField
            sx={{ width: '100%' }}
            label=""
            data-test={`${row?.id}-um`}
            value={row?.amountType || ''}
            variant="standard"
            onChange={event => {
              const { value } = event.target
              apiRef.current?.updateRows([{ ...row, amountType: value }])
            }}
            InputProps={{
              disableUnderline: true,
              sx: { fontSize: '0.875rem' },
            }}
            select
          >
            {FEE_OR_PROMOTION_UNITS_ARRAY.map(fp => (
              <MenuItem key={fp} value={fp}>
                {fp}
              </MenuItem>
            ))}
          </TextField>
        )
      },
    },
    {
      field: 'isActive',
      headerName: 'Active',
      renderCell: CheckboxCell,
      headerAlign: 'center',
      align: 'center',
      sortable: false,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      type: 'actions',
      getActions: params => [
        <GridActionsCellItem
          onClick={event => {
            event.stopPropagation()
            apiRef.current?.updateRows([{ id: params.row?.id, _action: 'delete' }])
            onDeleteRow(params.row?.id)
          }}
          label="Remove"
          showInMenu
        />,
      ],
    },
  ]
}

export const SUPPLIER_TYPE_SETTINGS_COLUMNS = ({ apiRef, onDeleteRow }) => {
  const { setDefaultTaxRate } = store.getState()?.featureFlags?.variables || {}
  return [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      sortable: false,
      renderCell: params => {
        const { row } = params

        if (isDefaultRow(params)) {
          return <Typography sx={{ fontSize: '0.875rem' }}>{row?.name || ''}</Typography>
        }
        return (
          <TextField
            sx={{ width: '100%' }}
            label=""
            data-test={`${row?.id}-name`}
            value={row?.name}
            variant="standard"
            onChange={event => {
              const { value } = event.target
              apiRef.current?.updateRows([{ ...row, name: value }])
            }}
            InputProps={{
              disableUnderline: true,
              sx: { fontSize: '0.875rem' },
            }}
          />
        )
      },
    },
    ...(setDefaultTaxRate
      ? [
          {
            field: 'taxRate',
            headerName: 'Tax Rate (%)',
            sortable: false,
            flex: 0.15,
            renderCell: params => {
              const { row } = params
              return (
                <TextField
                  sx={{ width: '100%' }}
                  label=""
                  data-test={`${row?.id}-taxRate`}
                  value={row?.taxRate}
                  variant="standard"
                  onChange={event => {
                    const { value } = event.target
                    apiRef.current?.updateRows([{ ...row, taxRate: value }])
                  }}
                  InputProps={{
                    inputComponent: InputFormat,
                    disableUnderline: true,
                    sx: { fontSize: '0.875rem' },
                  }}
                />
              )
            },
          },
        ]
      : []),
    {
      field: 'active',
      headerName: 'Active',
      renderCell: params => <CheckboxCell {...params} disabled={isDefaultRow(params)} />,
      headerAlign: 'center',
      align: 'center',
      sortable: false,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      type: 'actions',
      renderCell: params => {
        const { row } = params
        return (
          <IconButton
            color="error"
            sx={{
              fill: 'red',
            }}
            aria-label="delete"
            disabled={!row?.isNew}
            onClick={event => {
              event.stopPropagation()
              apiRef.current?.updateRows([{ id: params.row?.id, _action: 'delete' }])
              onDeleteRow(params.row?.id)
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        )
      },
    },
  ]
}

export const VENDOR_CLASS_SETTINGS_COLUMNS = ({ apiRef, onDeleteRow }) => [
  {
    field: 'name',
    headerName: 'Name',
    flex: 1,
    sortable: false,
    renderCell: params => {
      const { row } = params

      if (isDefaultRow(params)) {
        return <Typography sx={{ fontSize: '0.875rem' }}>{row?.name || ''}</Typography>
      }
      return (
        <TextField
          sx={{ width: '100%' }}
          label=""
          data-test={`${row?.id}-name`}
          value={row?.name}
          variant="standard"
          onChange={event => {
            const { value } = event.target
            apiRef.current?.updateRows([{ ...row, name: value }])
          }}
          InputProps={{
            disableUnderline: true,
            sx: { fontSize: '0.875rem' },
          }}
        />
      )
    },
  },
  {
    field: 'active',
    headerName: 'Active',
    renderCell: params => <CheckboxCell {...params} disabled={isDefaultRow(params)} />,
    headerAlign: 'center',
    align: 'center',
    sortable: false,
  },
  {
    field: 'actions',
    headerName: 'Actions',
    type: 'actions',
    renderCell: params => {
      const { row } = params
      return (
        <IconButton
          color="error"
          sx={{
            fill: 'red',
          }}
          aria-label="delete"
          disabled={!row?.isNew}
          onClick={event => {
            event.stopPropagation()
            apiRef.current?.updateRows([{ id: params.row?.id, _action: 'delete' }])
            onDeleteRow(params.row?.id)
          }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      )
    },
  },
]

export const CASH_DRAWERS_ACTIVITY_COLUMNS = () => [
  {
    field: 'paymentNumber',
    headerName: '#',
    flex: 1,
    editable: false,
    sortable: true,
  },
  {
    field: 'dateTime',
    headerName: 'Date/Time',
    flex: 1,
    editable: false,
    sortable: true,
  },
  {
    field: 'activityType',
    headerName: 'Activity Type',
    flex: 1,
    editable: false,
    sortable: true,
  },
  {
    field: 'amount',
    headerName: 'Amount',
    flex: 1,
    editable: false,
    sortable: true,
  },
  {
    field: 'notes',
    headerName: 'Notes',
    flex: 1,
    editable: false,
    sortable: true,
  },
]

export const ACCOUNTS_COLUMNS = ({ apiRef, featureFlagVariables }) => [
  {
    field: 'accountName',
    headerName: 'Account Name',
    flex: 1,
    sortable: true,
    renderCell: params => {
      const { row } = params
      if (row?.accountName === 'Default Cash Drawer') {
        return <Typography sx={{ fontSize: '0.875rem' }}>{row?.accountName || ''}</Typography>
      }
      return (
        <TextField
          sx={{ width: '100%' }}
          label=""
          data-test={`${row?.id}-accountName`}
          value={row?.accountName}
          variant="standard"
          onChange={event => {
            const { value } = event.target
            apiRef.current?.updateRows([{ ...row, accountName: value, edited: true }])
          }}
          InputProps={{
            disableUnderline: true,
            sx: { fontSize: '0.875rem' },
          }}
        />
      )
    },
  },
  {
    field: 'type',
    headerName: 'Payment Type',
    flex: 1,
    sortable: true,
    valueGetter: ({ value }) => ACCOUNT_PAYMENT_TYPES.find(type => type.id === value)?.name,
  },
  {
    field: 'ledgerCode',
    headerName: 'Ledger Code',
    flex: 1,
    sortable: true,
    renderCell: params => {
      const { row } = params
      return (
        <TextField
          sx={{ width: '100%' }}
          label=""
          data-test={`${row?.id}-ledgerCode`}
          value={row?.ledgerCode}
          variant="standard"
          onChange={event => {
            const { value } = event.target
            apiRef.current?.updateRows([{ ...row, ledgerCode: value, edited: true }])
          }}
          InputProps={{
            disableUnderline: true,
            sx: { fontSize: '0.875rem' },
          }}
        />
      )
    },
  },
]

export const DRIVER_COLUMNS = ({ apiRef, onDeleteRow }) => [
  {
    field: 'name',
    headerName: 'Name',
    flex: 1,
    sortable: false,
    renderCell: params => {
      const { row } = params

      if (isDefaultRow(params)) {
        return <Typography sx={{ fontSize: '0.875rem' }}>{row?.name || ''}</Typography>
      }
      return (
        <TextField
          sx={{ width: '100%' }}
          label=""
          data-test={`${row?.id}-name`}
          value={row?.name}
          variant="standard"
          onChange={event => {
            const { value } = event.target
            apiRef.current?.updateRows([{ ...row, name: value }])
          }}
          InputProps={{
            disableUnderline: true,
            sx: { fontSize: '0.875rem' },
          }}
        />
      )
    },
  },
  {
    field: 'isActive',
    headerName: 'Active',
    renderCell: params => <CheckboxCell {...params} disabled={isDefaultRow(params)} />,
    headerAlign: 'center',
    align: 'center',
    sortable: false,
  },
  {
    field: 'actions',
    headerName: 'Actions',
    type: 'actions',
    renderCell: params => {
      const { row } = params
      return (
        <IconButton
          color="error"
          sx={{
            fill: 'red',
          }}
          aria-label="delete"
          disabled={!row?.isNew}
          onClick={event => {
            event.stopPropagation()
            apiRef.current?.updateRows([{ id: params.row?.id, _action: 'delete' }])
            onDeleteRow(params.row?.id)
          }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      )
    },
  },
]

export const PAYMENT_TERMS_COLUMNS = ({ apiRef }) => [
  {
    field: 'display',
    headerName: 'Display',
    flex: 1,
    sortable: false,
    renderCell: params => {
      const { row } = params

      if (row.nonEditable) {
        return <Typography sx={{ fontSize: '0.875rem' }}>{row?.display || ''}</Typography>
      }
      return (
        <TextField
          sx={{ width: '100%' }}
          label=""
          data-test={`${row?.id}-display`}
          value={row?.display}
          variant="standard"
          onChange={event => {
            const { value } = event.target
            apiRef.current?.updateRows([{ ...row, display: value }])
          }}
          InputProps={{
            disableUnderline: true,
            sx: { fontSize: '0.875rem' },
          }}
        />
      )
    },
  },
  {
    field: 'days',
    headerName: 'Days',
    headerAlign: 'right',
    align: 'right',
    type: 'number',
    sortable: false,
    renderCell: params => {
      const { row } = params

      if (row.nonEditable) {
        return <Typography sx={{ fontSize: '0.875rem' }}>{row?.days || ''}</Typography>
      }
      return (
        <TextField
          sx={{ width: '100%' }}
          label=""
          data-test={`${row?.id}-days`}
          value={row?.days}
          variant="standard"
          onChange={event => {
            const { value } = event.target
            apiRef.current?.updateRows([{ ...row, days: parseInt(value, 10) || null }])
          }}
          InputProps={{
            disableUnderline: true,
            inputComponent: InputFormat,
            inputProps: {
              style: {
                fontSize: '0.875rem',
                textAlign: 'right',
              },
            },
          }}
        />
      )
    },
  },
  {
    field: 'active',
    headerName: 'Active',
    renderCell: params => <CheckboxCell {...params} disabled={params.row.nonEditable} />,
    headerAlign: 'center',
    align: 'center',
    sortable: false,
  },
]

export const ASSET_TYPES_SETTINGS_COLUMNS = ({ apiRef }) => [
  {
    field: 'name',
    headerName: 'Name',
    flex: 1,
    sortable: false,
    renderCell: params => {
      const { row } = params

      if (isDefaultRow(params)) {
        return <Typography sx={{ fontSize: '0.875rem' }}>{row?.name || ''}</Typography>
      }
      return (
        <TextField
          sx={{ width: '100%' }}
          label=""
          data-test={`${row?.id}-name`}
          value={row?.name}
          variant="standard"
          onChange={event => {
            const { value } = event.target
            apiRef.current?.updateRows([{ ...row, name: value }])
          }}
          InputProps={{
            disableUnderline: true,
            sx: { fontSize: '0.875rem' },
          }}
        />
      )
    },
  },
]
